/**
 * #2133 デザイン: サイドバー・ホームのカードで使う線画アイコン。
 * アイコンライブラリを依存に足さず、24x24 の stroke だけで描く。
 * 装飾なので常に aria-hidden(意味はとなりのラベルで伝える)。
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';

export type IconName =
  | 'home'
  | 'company'
  | 'studio'
  | 'contracts'
  | 'students'
  | 'instructors'
  | 'staff'
  | 'lessons'
  | 'sequences'
  | 'poses'
  | 'categories'
  | 'mats'
  | 'settings'
  | 'logout'
  | 'menu'
  | 'close'
  | 'plus'
  | 'chevronRight'
  | 'search';

const PATHS: Record<IconName, ReactNode> = {
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h5v-6h4v6h5V9.5" />
    </>
  ),
  company: (
    <>
      <rect x="4" y="3" width="11" height="18" rx="1" />
      <path d="M15 9h4a1 1 0 0 1 1 1v11h-5" />
      <path d="M8 7h3M8 11h3M8 15h3" />
    </>
  ),
  studio: (
    <>
      <path d="M3 9.5 12 4l9 5.5" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  contracts: (
    <>
      <path d="M6 2.5h9l3 3V21a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" />
      <path d="M15 2.5V6h3" />
      <path d="M8 10h8M8 13.5h8M8 17h5" />
    </>
  ),
  students: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6.5 6.5 0 0 1 3.5 6" />
    </>
  ),
  instructors: (
    <>
      <path d="m2 9 10-5 10 5-10 5Z" />
      <path d="M6 11v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
      <path d="M22 9v6" />
    </>
  ),
  staff: (
    <>
      <rect x="3" y="5" width="18" height="15" rx="2" />
      <circle cx="9" cy="11.5" r="2.5" />
      <path d="M5.5 17a3.5 3.5 0 0 1 7 0" />
      <path d="M15 10h3M15 14h3" />
      <path d="M9 5V3h6v2" />
    </>
  ),
  lessons: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M7.5 14h3M13.5 14h3M7.5 17.5h3" />
    </>
  ),
  sequences: (
    <>
      <circle cx="5" cy="6" r="1.5" />
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="5" cy="18" r="1.5" />
      <path d="M10 6h10M10 12h10M10 18h10" />
    </>
  ),
  poses: (
    <>
      <circle cx="12" cy="4.5" r="2" />
      <path d="M4 9.5 12 8l8 1.5" />
      <path d="M12 8v6l-4 7M12 14l4 7" />
    </>
  ),
  categories: (
    <>
      <rect x="3" y="4" width="8" height="8" rx="1.5" />
      <rect x="13" y="4" width="8" height="8" rx="1.5" />
      <rect x="3" y="14" width="8" height="8" rx="1.5" />
      <rect x="13" y="14" width="8" height="8" rx="1.5" />
    </>
  ),
  mats: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="2" />
      <path d="M4 10h16" />
      <circle cx="8" cy="14" r="1" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </>
  ),
  logout: (
    <>
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
      <path d="M10 16 4 12l6-4M4 12h11" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  chevronRight: <path d="m9 6 6 6-6 6" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m20 20-4.35-4.35" />
    </>
  ),
};

interface IconProps {
  name: IconName;
  className?: string;
}

export function Icon({ name, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={clsx('h-5 w-5 shrink-0', className)}
    >
      {PATHS[name]}
    </svg>
  );
}
